import { FC, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from 'src/pages/private';
import CustomerInformation from './CustomerInformation';
import OrderItems from './OrderItems';
import Discounts from './Discounts';
import Surcharges from './Surcharges';
import Summary from './Summary';
import OrderStepper from './OrderStepper';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { apiUrls, errorMessages } from 'src/utils/properties.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import { EditOrderData } from 'src/models/EditOrder.interface';
import {
  Container,
  FixedHeader,
  HeaderRow,
  BackButton,
  BackIcon,
  Title,
  Description,
  ContentBox,
} from '../CreateOrder/CreateOrder.styles';

const EditOrder: FC = () => {
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const [activeStep, setActiveStep] = useState(0);
  const [orderData, setOrderData] = useState<EditOrderData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { showSnackbar } = StyledSnackbar();

  useEffect(() => {
    if (orderId) {
      fetchOrderData();
    }
  }, [orderId]);

  const fetchOrderData = async () => {
    try {
      setIsLoading(true);
      const response = await axiosTokenInstance.get(
        `${apiUrls.GET_ORDER_BY_ID}/${orderId}`
      );
      setOrderData(response.data);
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage || errorMessages.FAILED_TO_LOAD_ORDER, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleNext = () => {
    if (activeStep < 4) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBackStep = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleStepClick = (step: number) => {
    setActiveStep(step);
  };

  if (isLoading) {
    return (
      <MainLayout title="Order Management">
        <Container>
          <FixedHeader>
            <HeaderRow>
              <BackButton onClick={handleBack}>
                <BackIcon className="bx bx-chevron-left" />
              </BackButton>
              <Title>Edit Order</Title>
            </HeaderRow>
            <Description>Loading order details...</Description>
          </FixedHeader>
        </Container>
      </MainLayout>
    );
  }

  if (!orderData) {
    return (
      <MainLayout title="Order Management">
        <Container>
          <FixedHeader>
            <HeaderRow>
              <BackButton onClick={handleBack}>
                <BackIcon className="bx bx-chevron-left" />
              </BackButton>
              <Title>Edit Order</Title>
            </HeaderRow>
            <Description>{errorMessages.ORDER_NOT_FOUND}</Description>
          </FixedHeader>
        </Container>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Order Management">
      <Container>
        <FixedHeader>
          <HeaderRow>
            <BackButton onClick={handleBack}>
              <BackIcon className="bx bx-chevron-left" />
            </BackButton>
            <Title>Edit Order</Title>
          </HeaderRow>
          <Description>
            {activeStep === 0 &&
              'Capture customer and delivery-related information.'}
            {activeStep === 1 &&
              'Add products, quantities, and pricing for this order.'}
            {activeStep === 2 && 'Optional price adjustments.'}
            {activeStep === 3 && 'Additional costs.'}
            {activeStep === 4 && 'Order Summary & Confirmation.'}
          </Description>
          <OrderStepper activeStep={activeStep} onStepClick={handleStepClick} />
        </FixedHeader>
        <ContentBox>
          {activeStep === 0 && (
            <CustomerInformation
              onNext={handleNext}
              orderId={orderId || ''}
              orderData={orderData}
            />
          )}
          {activeStep === 1 && (
            <OrderItems
              onNext={handleNext}
              onBack={handleBackStep}
              orderId={orderId || ''}
              orderData={orderData}
            />
          )}
          {activeStep === 2 && (
            <Discounts
              onNext={handleNext}
              onBack={handleBackStep}
              orderId={orderId || ''}
              orderData={orderData}
            />
          )}
          {activeStep === 3 && (
            <Surcharges
              onNext={handleNext}
              onBack={handleBackStep}
              orderId={orderId || ''}
              orderData={orderData}
            />
          )}
          {activeStep === 4 && (
            <Summary
              onBack={handleBackStep}
              orderId={orderId || ''}
              orderData={orderData}
            />
          )}
        </ContentBox>
      </Container>
    </MainLayout>
  );
};

export default EditOrder;
